import { MAP } from '../config.js';
import { LAYOUT_STRIDE as S } from './navLayers.js';

/**
 * navLayers の細かい見取り（10cm マス）を、地図として見やすい粗いマスに整える。
 *
 * スキャンから拾った壁は点線のように途切れ、床にも穴が空く。そのまま箱にすると
 * ギザギザで読みにくいので、
 * - マスを MAP.coarse 倍にまとめる
 * - 床の小さな穴を埋める
 * - 両側を壁に挟まれた1マスの隙間を壁で埋める
 * - 孤立した壁の点や、床から離れた壁を消す
 * - 壁の高さをそろえる
 * を行う。戻り値は layout と同じ形（[gx, gz, bottom, top, 色] の並び）。
 * 家具は形が細かいほど何か分かりやすいので、まとめずに元の細かいマスのまま返す（furnitureCell）。
 */
export function simplifyLayout(layout) {
  const F = MAP.coarse;
  const cell = layout.cell * F;
  const W = Math.ceil(layout.size / F);
  const at = (x, z) => (x < 0 || z < 0 || x >= W || z >= W ? -1 : z * W + x);

  const floors = groupFloors(layout.floors, F, W);
  const baseFloor = new Float32Array(W * W).fill(NaN);
  for (const [k, levels] of floors) baseFloor[k] = levels[0].top;
  const floorColorNear = (k) => floors.get(k)?.[0].color;

  fillFloorHoles(floors, baseFloor, W, at, floorColorNear);

  const wall = new Uint8Array(W * W);
  const wallBase = new Float32Array(W * W).fill(NaN);
  const wallColors = new Map();
  for (let i = 0; i < layout.walls.length; i += S) {
    const k = at(Math.floor(layout.walls[i] / F), Math.floor(layout.walls[i + 1] / F));
    if (k < 0 || floors.has(k)) continue;
    wall[k] = 1;
    wallBase[k] = Number.isNaN(wallBase[k]) ? layout.walls[i + 2] : Math.min(wallBase[k], layout.walls[i + 2]);
    wallColors.set(k, [...(wallColors.get(k) ?? []), layout.walls[i + 4]]);
  }
  closeWallGaps(wall, wallBase, wallColors, floors, W, at);
  removeStrayWalls(wall, floors, W, at);

  const out = { floors: [], walls: [] };
  for (const [k, levels] of floors) {
    // 床はスキャンの色だとムラが目立つので、上の階ほど明るい木目色で統一する
    for (const { bottom, top } of levels) {
      // スキャンの床の波打ち（ラグの厚み等）で段々に見えないよう、一番下の床に近い高さは平らにそろえる
      const flat = top - layout.baseY <= MAP.flattenRise;
      const y = flat ? layout.baseY : top;
      out.floors.push(k % W, (k / W) | 0, flat ? y - MAP.slab : bottom, y, floorTone(y - layout.baseY));
    }
  }
  for (let k = 0; k < W * W; k += 1) {
    if (!wall[k]) continue;
    const base = Number.isNaN(wallBase[k]) ? layout.baseY : wallBase[k];
    out.walls.push(k % W, (k / W) | 0, base, base + MAP.wallHeight, MAP.colors.wall);
  }

  // 家具は細かいマスのまま。粗いマスで壁になった所に重なるものだけ除く
  const furniture = [];
  for (let i = 0; i < layout.furniture.length; i += S) {
    const k = at(Math.floor(layout.furniture[i] / F), Math.floor(layout.furniture[i + 1] / F));
    if (k >= 0 && wall[k]) continue;
    furniture.push(...layout.furniture.slice(i, i + S));
  }

  return {
    ...out,
    furniture,
    furnitureCell: layout.cell,
    cell,
    minX: layout.minX,
    minZ: layout.minZ,
    baseY: layout.baseY,
  };
}

/**
 * 粗いマスごとに床の高さをまとめる。同じ粗いマスに1階とロフトのように離れた高さがあれば別の段として残す。
 * 細かいマスの半分以上に床がある高さだけを採用する（壁際のはみ出しを消す）。
 */
function groupFloors(flat, F, W) {
  const cells = new Map();
  for (let i = 0; i < flat.length; i += S) {
    const k = Math.floor(flat[i + 1] / F) * W + Math.floor(flat[i] / F);
    const list = cells.get(k) ?? [];
    list.push({ bottom: flat[i + 2], top: flat[i + 3], color: flat[i + 4] });
    cells.set(k, list);
  }

  const floors = new Map();
  const need = Math.ceil((F * F) / 2);
  for (const [k, list] of cells) {
    list.sort((a, b) => a.top - b.top);
    const levels = [];
    let group = [];
    const flush = () => {
      if (group.length >= need) {
        const mid = group[Math.floor(group.length / 2)];
        levels.push({
          bottom: Math.min(...group.map((g) => g.bottom)),
          top: mid.top,
          color: mixColors(group.map((g) => g.color)),
        });
      }
      group = [];
    };
    for (const item of list) {
      if (group.length > 0 && item.top - group[group.length - 1].top > MAP.levelGap) flush();
      group.push(item);
    }
    flush();
    if (levels.length > 0) floors.set(k, levels);
  }
  return floors;
}

/** 上下左右のうち3方以上が近い高さの床に囲まれた穴を、床で埋める */
function fillFloorHoles(floors, baseFloor, W, at, colorNear) {
  for (let pass = 0; pass < MAP.fillPasses; pass += 1) {
    const added = [];
    for (let k = 0; k < W * W; k += 1) {
      if (floors.has(k)) continue;
      const x = k % W;
      const z = (k / W) | 0;
      const near = NEIGHBORS.map(([dx, dz]) => at(x + dx, z + dz)).filter((n) => n >= 0 && !Number.isNaN(baseFloor[n]));
      if (near.length < 3) continue;
      const around = near.map((n) => baseFloor[n]).sort((a, b) => a - b);
      const h = around[1];
      if (around.filter((v) => Math.abs(v - h) <= MAP.levelGap).length < 3) continue;
      added.push([k, h, mixColors(near.map(colorNear).filter((c) => c !== undefined))]);
    }
    for (const [k, h, color] of added) {
      floors.set(k, [{ bottom: h - MAP.slab, top: h, color }]);
      baseFloor[k] = h;
    }
    if (added.length === 0) break;
  }
}

/** 左右（または上下）を壁に挟まれた、床の無い1〜2マスの隙間を壁で埋める */
function closeWallGaps(wall, wallBase, wallColors, floors, W, at) {
  const added = [];
  for (let k = 0; k < W * W; k += 1) {
    if (wall[k] || floors.has(k)) continue;
    const x = k % W;
    const z = (k / W) | 0;
    for (const [dx, dz] of [[1, 0], [0, 1]]) {
      for (const span of [1, 2]) {
        const a = at(x - dx, z - dz);
        const b = at(x + dx * span, z + dz * span);
        if (a < 0 || b < 0 || !wall[a] || !wall[b]) continue;
        if (span === 2) {
          const mid = at(x + dx, z + dz);
          if (floors.has(mid)) continue;
        }
        added.push([k, wallBase[a], wallColors.get(a) ?? []]);
      }
    }
  }
  for (const [k, base, list] of added) {
    wall[k] = 1;
    wallBase[k] = base;
    wallColors.set(k, list);
  }
}

/** 周り8マスに壁が無い点、および近くに床が無い壁を消す（部屋の外のゴミ） */
function removeStrayWalls(wall, floors, W, at) {
  const remove = [];
  const r = MAP.wallFloorReach;
  for (let k = 0; k < W * W; k += 1) {
    if (!wall[k]) continue;
    const x = k % W;
    const z = (k / W) | 0;
    let wallNeighbors = 0;
    let nearFloor = false;
    for (let dz = -r; dz <= r; dz += 1) {
      for (let dx = -r; dx <= r; dx += 1) {
        if (dx === 0 && dz === 0) continue;
        const n = at(x + dx, z + dz);
        if (n < 0) continue;
        if (Math.abs(dx) <= 1 && Math.abs(dz) <= 1 && wall[n]) wallNeighbors += 1;
        if (floors.has(n)) nearFloor = true;
      }
    }
    if (wallNeighbors === 0 || !nearFloor) remove.push(k);
  }
  for (const k of remove) wall[k] = 0;
}

const NEIGHBORS = [[1, 0], [-1, 0], [0, 1], [0, -1]];

/** 色（0xRRGGBB）の平均 */
function mixColors(list) {
  if (list.length === 0) return MAP.colors.wall;
  let r = 0;
  let g = 0;
  let b = 0;
  for (const c of list) {
    r += (c >> 16) & 255;
    g += (c >> 8) & 255;
    b += c & 255;
  }
  const n = list.length;
  return (Math.round(r / n) << 16) | (Math.round(g / n) << 8) | Math.round(b / n);
}

/** 床の高さ（一番下の床から）に応じた木目色。上の階（中二階・ロフト）ほど明るい */
function floorTone(rise) {
  const t = Math.min(1, Math.max(0, rise / 2.5));
  const a = MAP.colors.floor;
  const b = MAP.colors.floorUpper;
  const ch = (shift) => Math.round(((a >> shift) & 255) + ((((b >> shift) & 255) - ((a >> shift) & 255)) * t));
  return (ch(16) << 16) | (ch(8) << 8) | ch(0);
}
